import { useEffect, useState } from 'react';
import { apiGet, AdminForbiddenError } from '../lib/api';

type Engine = {
  slug: string;
  name: string;
  version: string;
  status: string;
  multi_tenant_ready: boolean;
  updated_at: string;
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { dateStyle: 'medium' });
}

export function Engines() {
  const [engines, setEngines] = useState<Engine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    apiGet<{ engines: Engine[] }>('/api/admin/engines')
      .then(({ engines: rows }) => setEngines(rows))
      .catch((err: unknown) => {
        if (err instanceof AdminForbiddenError) {
          setForbidden(true);
        } else {
          setError(err instanceof Error ? err.message : 'unknown error');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500">
        Loading…
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="rounded-lg border border-red-700/40 bg-red-900/10 p-6 text-center">
        <p className="text-red-400">
          You don&apos;t have admin access. Sign in with an admin account.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-700/40 bg-red-900/10 p-6 text-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (engines.length === 0) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-6 text-center text-slate-500">
        No engines registered
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-800 bg-slate-900/60 text-left text-xs uppercase tracking-widest text-slate-400">
            <th className="px-4 py-3">Slug</th>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Version</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Multi-tenant ready</th>
            <th className="px-4 py-3">Updated</th>
          </tr>
        </thead>
        <tbody>
          {engines.map((e) => (
            <tr
              key={e.slug}
              className="border-b border-slate-800/60 hover:bg-slate-900/40 transition-colors"
            >
              <td className="px-4 py-3 font-mono text-slate-300">{e.slug}</td>
              <td className="px-4 py-3">{e.name}</td>
              <td className="px-4 py-3 font-mono text-slate-400">{e.version}</td>
              <td className="px-4 py-3">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    e.status === 'active'
                      ? 'bg-emerald-900/40 text-emerald-400'
                      : e.status === 'beta'
                        ? 'bg-amber-900/40 text-amber-400'
                        : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {e.status}
                </span>
              </td>
              <td className="px-4 py-3">
                {e.multi_tenant_ready ? (
                  <span className="text-emerald-400">Yes</span>
                ) : (
                  <span className="text-slate-500">No</span>
                )}
              </td>
              <td className="px-4 py-3 text-slate-400">{formatDate(e.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
